// Phase JA.2 — Daily Acca Signal Menu.
//
// Renders `daily_acca_signals` rows fetched by `useDailyAccaSignals`,
// grouped by tier (A → B → C). Each row composes existing I0
// primitives only (Badge, Pill, Stat, StrengthDots, Countdown,
// CategoryChip, Eyebrow). LegRow is not used here because the
// signal-level row layout is denser (kickoff Countdown + tier badge +
// conviction dots + Stat) than the slip leg layout — but the row
// composition follows the same visual grammar.
//
// Promotion-not-restriction: anon users see tier-A signals with a
// non-blocking soft upsell hint ("Pro members see all 5 agent votes")
// rendered alongside, never replacing, the row. No fabrication.

import type { CSSProperties } from "react";
import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import { Badge, CategoryChip, Countdown, Eyebrow, Pill, Stat, StrengthDots } from "../super-acca";
import type { BadgeVariant } from "../super-acca";
import type { DailyAccaSignal, SignalTier } from "../../free-tier/useDailyAccaSignals.ts";
import { convictionToDots, tierLabel } from "./signalMenuFilters.ts";

const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";
const fontStat = "var(--sa-font-stat)";

function tierVariant(tier: SignalTier): BadgeVariant {
  if (tier === "tier_a") return "amber";
  if (tier === "tier_b") return "info";
  return "ghost";
}

function fmtKickoff(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "";
  }
}

export interface SignalMenuProps {
  signals: readonly DailyAccaSignal[];
  loading: boolean;
  /** When true, anon-only soft upsell hint renders next to tier-A rows. */
  showAnonUpsell?: boolean;
  /** ISO timestamp of the next publication (09:00 WAT). Empty-state countdown. */
  nextPublishISO: string;
  /** Phase JA.4 — when supplied, each signal row renders a "+ ADD TO SLIP" Pill. */
  onAddToSlip?: (signal: DailyAccaSignal) => void;
  /** Phase JA.4 — set of signal_ids currently in the user's draft slip. */
  draftSignalIds?: ReadonlySet<string>;
  /**
   * Phase JB.2 — Escalating Menu (§9). Free users see this many tier-A
   * rows without the upsell hint (celebratory unlock). Defaults to 0.
   */
  tierAUnlocks?: number;
  /**
   * Phase JB.2 — Celebratory unlock copy from useEscalatingMenu (N08).
   */
  unlockCopy?: string;
  style?: CSSProperties;
}

const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 24,
};

export function SignalMenu({
  signals,
  loading,
  showAnonUpsell,
  nextPublishISO,
  onAddToSlip,
  draftSignalIds,
  tierAUnlocks = 0,
  unlockCopy,
  style,
}: SignalMenuProps): JSX.Element {
  if (loading) {
    return (
      <div style={{ ...cardStyle, ...style }}>
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 10.5,
            letterSpacing: "0.22em",
            color: "var(--sa-fg-muted)",
          }}
        >
          LOADING TODAY'S SIGNALS…
        </div>
      </div>
    );
  }

  if (signals.length === 0) {
    return (
      <div style={{ ...cardStyle, ...style }} data-testid="signal-menu-empty">
        <Eyebrow>Signal Menu · Today</Eyebrow>
        <div
          style={{
            fontFamily: fontBody,
            fontSize: 14,
            color: "var(--sa-fg-secondary)",
            marginTop: 14,
            lineHeight: 1.7,
          }}
        >
          <p>Today's signals publish at 09:00 WAT. STRIKZA never fabricates rows.</p>
        </div>
        <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 16 }}>
          <span
            style={{
              fontFamily: fontMono,
              fontSize: 10.5,
              letterSpacing: "0.18em",
              color: "var(--sa-fg-muted)",
            }}
          >
            NEXT PUBLISH IN
          </span>
          <Countdown targetISO={nextPublishISO} compact />
        </div>
      </div>
    );
  }

  // Group by tier preserving the upstream sort (tier A → B → C, then kickoff).
  const groups = new Map<SignalTier, DailyAccaSignal[]>();
  for (const s of signals) {
    const arr = groups.get(s.tier) ?? [];
    arr.push(s);
    groups.set(s.tier, arr);
  }
  const tierOrder: SignalTier[] = ["tier_a", "tier_b", "tier_c"];

  return (
    <div style={{ ...cardStyle, ...style }} data-testid="signal-menu-populated">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Eyebrow>Signal Menu · Today</Eyebrow>
        <span style={{ fontFamily: fontMono, fontSize: 11, color: "var(--sa-fg-muted)" }}>
          {signals.length} SIGNAL{signals.length === 1 ? "" : "S"}
        </span>
      </div>
      <div style={{ marginTop: 8 }}>
        {tierOrder.map((tier) => {
          const rows = groups.get(tier);
          if (!rows || rows.length === 0) return null;
          return (
            <div key={tier} style={{ marginTop: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Badge variant={tierVariant(tier)}>{tierLabel(tier)}</Badge>
                <span style={{ fontFamily: fontMono, fontSize: 10, color: "var(--sa-fg-muted)" }}>
                  {rows.length}
                </span>
                {tier === "tier_a" && Boolean(showAnonUpsell) && tierAUnlocks > 0 && unlockCopy ? (
                  <span
                    data-testid="signal-menu-escalating-copy"
                    style={{
                      fontFamily: fontMono,
                      fontSize: 10,
                      color: "var(--sa-accent-saffron)",
                      letterSpacing: "0.16em",
                      marginLeft: "auto",
                    }}
                  >
                    {unlockCopy.toUpperCase()}
                  </span>
                ) : null}
              </div>
              <div style={{ marginTop: 4 }}>
                {rows.map((s, idx) => {
                  // For tier-A: the first `tierAUnlocks` rows are unlocked
                  // for free users (celebratory) — no upsell hint shown.
                  const isUnlockedForFree =
                    s.tier === "tier_a" && Boolean(showAnonUpsell) && idx < tierAUnlocks;
                  return (
                    <SignalRow
                      key={s.signal_id}
                      signal={s}
                      showAnonUpsell={
                        Boolean(showAnonUpsell) && s.tier === "tier_a" && !isUnlockedForFree
                      }
                      onAddToSlip={onAddToSlip}
                      inDraft={draftSignalIds?.has(s.signal_id) ?? false}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SignalRowProps {
  signal: DailyAccaSignal;
  showAnonUpsell: boolean;
  onAddToSlip?: (signal: DailyAccaSignal) => void;
  inDraft?: boolean;
}

function settlementBadgeVariant(outcome: DailyAccaSignal["outcome"]): BadgeVariant | null {
  if (outcome === "won") return "success";
  if (outcome === "lost") return "danger";
  if (outcome === "void") return "ghost";
  return null;
}

function settlementBadgeLabel(outcome: DailyAccaSignal["outcome"]): string {
  if (outcome === "won") return "WON";
  if (outcome === "lost") return "LOST";
  if (outcome === "void") return "VOID";
  return "";
}

function SignalRow({ signal, showAnonUpsell, onAddToSlip, inDraft }: SignalRowProps): JSX.Element {
  const dots = convictionToDots(signal.conviction_score);
  const founders = signal.is_founders_pick;
  const settled = settlementBadgeVariant(signal.outcome);

  return (
    <div
      data-testid="signal-row"
      data-tier={signal.tier}
      style={{
        padding: "14px 0",
        borderTop: "1px solid var(--sa-hairline)",
        display: "grid",
        gridTemplateColumns: "minmax(140px, 1.4fr) minmax(180px, 1.6fr) auto auto auto",
        gap: 16,
        alignItems: "center",
      }}
    >
      <div>
        <div style={{ fontFamily: fontBody, fontSize: 14, color: "var(--sa-fg-primary)", fontWeight: 500 }}>
          {signal.fixture_name}
        </div>
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 10.5,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.12em",
            marginTop: 4,
          }}
        >
          {(signal.league ?? "—")} · {fmtKickoff(signal.kickoff_at)}
        </div>
        {founders ? (
          <div style={{ marginTop: 6 }}>
            <CategoryChip category="PLAYER" />
          </div>
        ) : null}
      </div>
      <div>
        <div style={{ fontFamily: fontBody, fontSize: 13.5, color: "var(--sa-fg-primary)" }}>
          {marketLabel(signal.market)}
        </div>
        <div style={{ fontFamily: fontMono, fontSize: 11.5, color: "var(--sa-accent-saffron)", marginTop: 4 }}>
          {selectionLabel(signal.market, signal.selection)}
        </div>
        {founders ? (
          <div style={{ marginTop: 6 }}>
            <Badge variant="amber">FOUNDER'S PICK</Badge>
          </div>
        ) : null}
        {settled ? (
          <div style={{ marginTop: 6 }} data-testid="signal-row-settlement">
            <Badge variant={settled}>{settlementBadgeLabel(signal.outcome)}</Badge>
          </div>
        ) : null}
        {onAddToSlip && signal.outcome === "pending" ? (
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => onAddToSlip(signal)}
              disabled={inDraft}
              data-testid="signal-row-add-to-slip"
              style={{
                background: "transparent",
                border: 0,
                padding: 0,
                cursor: inDraft ? "default" : "pointer",
              }}
            >
              <Pill
                color={inDraft ? "var(--sa-fg-muted)" : "var(--sa-accent-saffron)"}
                borderColor={inDraft ? "var(--sa-hairline)" : "var(--sa-accent-saffron)"}
              >
                {inDraft ? "✓ IN SLIP" : "+ ADD TO SLIP"}
              </Pill>
            </button>
          </div>
        ) : null}
        {showAnonUpsell ? (
          <div
            title="Pro members see all 5 agent votes"
            style={{
              marginTop: 8,
              fontFamily: fontMono,
              fontSize: 10,
              letterSpacing: "0.16em",
              color: "var(--sa-accent-saffron)",
              cursor: "help",
            }}
          >
            ◇ PRO · ALL 5 AGENT VOTES
          </div>
        ) : null}
      </div>
      <div style={{ minWidth: 96 }}>
        <span style={{ fontFamily: fontMono, fontSize: 10, color: "var(--sa-fg-muted)", letterSpacing: "0.16em" }}>
          KICKS OFF
        </span>
        <div style={{ marginTop: 4 }}>
          <Countdown targetISO={signal.kickoff_at} compact />
        </div>
      </div>
      <div style={{ minWidth: 92 }}>
        <Stat
          label="Conviction"
          value={
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              {signal.conviction_score ?? "—"}
              <StrengthDots n={dots} />
            </span>
          }
        />
      </div>
      <div style={{ textAlign: "right", minWidth: 64 }}>
        <span style={{ fontFamily: fontStat, fontSize: 22, color: "var(--sa-fg-primary)", lineHeight: 1 }}>
          {signal.odds.toFixed(2)}
          <span style={{ fontSize: 11, color: "var(--sa-fg-muted)" }}>×</span>
        </span>
        {signal.clv_edge_detected ? (
          <div style={{ marginTop: 4 }}>
            <Pill color="var(--sa-signal-green)" borderColor="var(--sa-signal-green)">
              CLV+
              {signal.clv_edge_pct !== null ? ` ${signal.clv_edge_pct.toFixed(1)}%` : ""}
            </Pill>
          </div>
        ) : null}
      </div>
    </div>
  );
}
